import {useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import type {DependencyList, RefObject} from 'react';

const NEAR_BOTTOM_THRESHOLD_PX = 80;

/**
 * Pins a scroll viewport to the bottom as `deps` change; a `scroll` listener
 * unpins only when the user scrolls up (programmatic scrollTop keeps the pin).
 */
export const useStickToBottom = (
  viewportRef: RefObject<HTMLDivElement>,
  deps: DependencyList
): (() => void) => {
  const pinnedRef = useRef(true);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onScroll = () => {
      const distance =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      pinnedRef.current = distance <= NEAR_BOTTOM_THRESHOLD_PX;
    };
    viewport.addEventListener('scroll', onScroll);
    return () => viewport.removeEventListener('scroll', onScroll);
  }, [viewportRef]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !pinnedRef.current) return;
    viewport.scrollTop = viewport.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return useCallback(() => {
    pinnedRef.current = true;
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [viewportRef]);
};
